import { identity, once } from 'lodash'
import { action, computed, makeObservable, observable, reaction } from 'mobx'
import { Task, task } from 'mobx-task'
import { distinctUntilChanged, Observable } from 'rxjs'
import { SearchInventoryItems } from 'admin/InventoryStore'
import { AlertFactory, AlertState } from 'alerts/Alert'
import { isFuture } from 'date-fns'
import { ConfirmViewModel } from 'components/ConfirmDialog/ConfirmViewModel'
import { TableSortOrder } from 'components/Table'
import Member from 'iam/models/Member'
import { ProviderActivePtinStatusDto } from 'iam/models/ProviderActivePtinStatusDto'
import { UserDataType } from 'iam/models/UserDataType'
import { GetCurrentMember } from 'iam/SessionStore'
import {
  FetchUserTeamMembers,
  FilterTeamMembers,
  FindUserTeamMembersByUserPublicId,
} from 'iam/TeamMemberStore'
import { FindUserTeams } from 'iam/TeamStore'
import UserDataStore from 'iam/UserDataStore'
import { GetCurrentUserWorkspaceMemberships } from 'iam/WorkspaceMembershipStore'
import { getFeatureToggleClient } from 'infra/featureToggles'
import { FlagFilter } from 'infra/FilterControls/Boolean/FlagFilter'
import { SelectFilter } from 'infra/FilterControls/Select/SelectFilter'
import { FilterStorageFactory } from 'infra/FiltersViewModel'
import { Storage, localStorage as appStorage } from 'infra/storage'
import {
  LastWorkspaceStorage,
  ALL_WORKSPACES_SELECTION,
} from 'infra/LastWorkspace'
import JobSearchAdapter from 'jobs/infra/JobSearchAdapter'
import JobSearchViewModel, {
  TicketsAdapter,
} from 'jobs/infra/JobSearchViewModel'
import { JobsLoadFilterItemsAdapter } from 'jobs/infra/JobsLoadFilterItemsAdapter'
import JobsTableColumnManager from 'jobs/infra/JobsTableColumnManager'
import {
  MutableColumnDetails,
  StaticColumnDetails,
} from 'jobs/infra/JobsTableColumns'
import { FindLegends } from 'legends/PublishedLegendStore'
import { AutomatedAction } from 'opportunities/offers/OpportunityOffersViewModel'
import JobStore from '../../JobStore'
import SearchableJob from '../../SearchableJob'
import { JobTicketStore } from '../../tickets/JobTicketStore'
import { tapAction } from 'utils/mobx-util'
import { isNotNil } from 'utils/object-util'
import { pluralize } from 'utils/pluralization-util'
import WorkspaceSelectorState from 'iam/views/AuthenticationContainer/WorkspaceSelector/WorkspaceSelectorState'
import { Id } from 'types/definitions'
import BulkCopyJobsViewModel from '../BulkCopyJobDialog/BulkCopyJobViewModel'
import TransferJobViewModel from '../JobRoot/Job/TransferJobDialog/TransferJobViewModel'
import { FindMembers } from '../../../iam/MemberStore'
import ProviderStore from '../../ProviderStore'
import BulkActionJobErrorViewModel from '../BulkActionJobErrorDialog/BulkActionJobErrorViewModel'
import BulkCopyJobsSuccessViewModel from '../BulkCopyJobsSuccessDialog/BulkCopyJobsSuccessViewModel'
import JobCreationDialogViewModel from '../../creation/views/JobCreationDialog/JobCreationDialogViewModel'
import UpcomingConsultationPreviewViewModel from '../UpcomingConsultationPreview/UpcomingConsultationPreviewViewModel'
import ConsultationStore from 'jobs/consultations/ConsultationStore'
import { NotifyProsOfUnclaimedJobsViewModel } from './NotifyProsOfUnclaimedJobsDialog/NotifyProsOfUnclaimedJobsViewModel'
import { FetchDocumentTypes } from 'documents/DocumentTypeStore'

/**
 * States permitted for the Archive/Restore Jobs Action. Currently, this allows multi-selecting
 * jobs to archive or restore.
 */
export type ArchiveRestoreAction = Archive | Restore | NoJobsSelected

/**
 * Indicates that jobs should be archived.
 */
export type Archive = {
  type: 'Archive'
}

/**
 * Indicates that jobs should be restored.
 */
export type Restore = {
  type: 'Restore'
}

/**
 * Indicates that no jobs are selected.
 */
export type NoJobsSelected = {
  type: 'NoJobsSelected'
}

/**
 * Checkbox selection states.
 */
export const enum SelectionState {
  /**
   * Nothing is selected at all.
   */
  NoneSelected = 'NoneSelected',

  /**
   * Some are selected.
   */
  SomeSelected = 'SomeSelected',

  /**
   * All are selected.
   */
  AllSelected = 'AllSelected',
}

export type JobManagementViewModelFactory = (
  storageKey: string,
) => JobManagementViewModel

/**
 * Job Management view model.
 */
export default class JobManagementViewModel {
  /**
   * Storage for the last selected workspace(s).
   */
  private lastWorkspaceStorage = new LastWorkspaceStorage(
    this.localStorage,
    'jobManagement',
  )

  private readonly globalSnapshotKey = 'globalWorkspaceSnapshot.jobManagement'

  /**
   * Disposer for the workspace filter reaction.
   */
  private workspaceReactionDisposer: (() => void) | null = null

  /**
   * Disposer for the global workspace selector reaction.
   */
  private globalWorkspaceReactionDisposer: (() => void) | null = null

  /**
   * Adapter to load filter items.
   */
  loadFilterItemsAdapter = new JobsLoadFilterItemsAdapter(
    this.getCurrentUserWorkspaceMemberships,
    this.findLegends,
    this.findUserTeams,
    this.fetchServiceItems,
    this.fetchDocumentTypes,
  )

  /**
   * Adapter for retrieving tickets.
   */
  ticketsAdapter: TicketsAdapter = {
    fetchLatestOpenTicketsForJobs:
      this.jobTicketStore.fetchLatestOpenTicketsForJobs,
  }

  /**
   * Job Searcher.
   */
  searcher = new JobSearchViewModel(
    this.jobFetcher,
    this.loadFilterItemsAdapter,
    this.ticketsAdapter,
    this.newAlert,
    // Don't apply client-side filter inside the searcher.
    // Otherwise we would end up showing a Completely Empty State
    // when there may very well be jobs in the search result.
    identity,
    this.getCurrentMember,
    this.userDataStore,
    this.getCurrentUserWorkspaceMemberships,
    this.filterTeamMembers,
    this.createFilterStorage(this.storageKey),
  )

  /**
   * Ensure we only activate the searcher once, as we want to keep it around
   * for subsequent page views of job management.
   */
  private activateSearcher = once(() => this.searcher.activate())

  /**
   * Column manager.
   */
  columnManager = new JobsTableColumnManager(
    this.localStorage,
    this.searcher.getAllFilteredJobs,
    this.getCurrentMember,
    this.getCurrentUserWorkspaceMemberships,
    this.newAlert,
    this.userDataStore,
    () => this.selectedWorkspaces,
    this.storageKey,
  )

  selectionVM = new JobManagementSelectionViewModel(
    () => this.filteredJobs,
    this.newAlert,
    this.jobStore,
    this.getCurrentUserWorkspaceMemberships,
    () => this.selectedWorkspaces,
    () => this.archivedJobsFilter.value,
    this.searcher.removeJobs,
    this.jobFetcher,
    (job) => this.pushJob(job),
    this.searcher.fetchAllJobs,
    () => this.searcher.workspacesFilter,
    this.findMembers,
    this.findTeamMembersByUserPublicId,
    this.providerStore,
    this.jobCreationDialogViewModel,
  )

  upcomingConsultationPreviewVM = new UpcomingConsultationPreviewViewModel(
    this.consultationStore,
    () =>
      this.userDataStore.getByPublicId(this.getCurrentMember().user.publicId),
  )

  notifyProsOfUnclaimedJobsVM = new NotifyProsOfUnclaimedJobsViewModel(
    this.jobStore,
    this.newAlert,
    () => this.selectedWorkspaces,
  )

  /**
   * Update workspaces filter when current workspace is changed.
   */
  private workspaceSelectorUpdates$ = this.currentMember$.pipe(
    distinctUntilChanged(),
    tapAction((member) => {
      if (
        this.searcher.workspacesFilter.options.some(
          (o) => o.value === member.workspace.id,
        ) &&
        !this.workspaceSelectorState.isDefaultToAllWorkspaces
      ) {
        this.searcher.workspacesFilter.change([member.workspace.id])
      }
    }),
  )

  /**
   * Constructor.
   */
  constructor(
    private jobFetcher: JobSearchAdapter,
    private newAlert: AlertFactory,
    private findLegends: FindLegends,
    private getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMemberships,
    private fetchUserTeamMembers: FetchUserTeamMembers,
    private currentMember$: Observable<Member>,
    private getCurrentMember: GetCurrentMember,
    private jobTicketStore: JobTicketStore,
    private createFilterStorage: FilterStorageFactory,
    private localStorage: Storage,
    private userDataStore: UserDataStore,
    private filterTeamMembers: FilterTeamMembers,
    private _canEditDraftJobAsAdmin: () => boolean,
    private findUserTeams: FindUserTeams,
    private _useEditDraftJobForFirmAdmin: () => boolean,
    public handleOfferResponse: (action: AutomatedAction) => Promise<void>,
    private jobStore: JobStore,
    private storageKey: string,
    private fetchServiceItems: SearchInventoryItems,
    private workspaceSelectorState: WorkspaceSelectorState,
    private providerStore: ProviderStore,
    private findTeamMembersByUserPublicId: FindUserTeamMembersByUserPublicId,
    private findMembers: FindMembers,
    private jobCreationDialogViewModel: JobCreationDialogViewModel,
    private consultationStore: ConsultationStore,
    private fetchDocumentTypes: FetchDocumentTypes,
  ) {
    makeObservable(this)
  }

  /**
   * Computes whether an active PTIN is required for the current member.
   *
   * @returns {boolean} Returns true if an active PTIN is required, otherwise false.
   */
  @computed
  get ptinRequired(): boolean {
    const featureEnabled = getFeatureToggleClient().variation(
      'Worklayer.RequireActivePTIN',
    )
    if (!featureEnabled) {
      return false
    }

    const userData = this.userDataStore.getByPublicId(
      this.getCurrentMember().user.publicId,
    )

    return (
      userData?.userDataType === UserDataType.Pro &&
      userData?.ptinStatus === ProviderActivePtinStatusDto.Inactive
    )
  }

  /**
   * Jobs returned by the searcher.
   */
  @computed
  get filteredJobs() {
    return this.searcher.jobs
  }

  /**
   * Whether we are showing the empty job view
   */
  @computed
  get showEmptyView() {
    return (
      this.filteredJobs.length === 0 &&
      !this.searcher.scroller.fetch.pending &&
      this.searcher.scroller.isDone
    )
  }

  /**
   * Whether the user can edit draft job as admin.
   */
  @computed
  get canEditDraftJobAsAdmin() {
    return this._canEditDraftJobAsAdmin()
  }

  /**
   * The workspaces the user has selected.
   */
  @computed
  get selectedWorkspaces() {
    const selectedWorkspacesFilter = this.searcher.filters.getFilter(
      'workspaces',
    ) as SelectFilter<'workspaces', string>
    return selectedWorkspacesFilter.selected
  }

  @computed
  get canNotifyProsOfUnclaimedJobs() {
    if (this.selectedWorkspaces.length !== 1) {
      return false
    }

    const workspaceId = this.selectedWorkspaces[0]
    const membership = this.getCurrentUserWorkspaceMemberships().find(
      (m) => m.workspace.id === workspaceId,
    )

    return membership?.member.hasPermission('ADMIN_JOB_UPDATE') ?? false
  }

  @computed
  get archivedJobsFilter() {
    return this.searcher.filters.getFilter('archived') as FlagFilter<'archived'>
  }

  /**
   * Whether the current member is a firm admin
   */
  @computed
  get useEditDraftJobForFirmAdmin() {
    return this._useEditDraftJobForFirmAdmin()
  }

  /**
   * Shows whether the jobs are active or achived
   */
  @computed
  get jobViewingActionsLabel() {
    return !this.searcher.filters.getFilter('archived')?.value
      ? 'Active jobs'
      : 'Inactive jobs'
  }

  /**
   * Gets the sort order for the specified column.
   *
   * @param column
   * @returns
   */
  getSortOrder(
    column: StaticColumnDetails & MutableColumnDetails,
  ): TableSortOrder {
    return this.searcher.sorting.type === 'Field' &&
      this.searcher.sorting.field === column.field
      ? this.searcher.sorting.direction
      : 'none'
  }

  /**
   * Sets the sort order for the specified column.
   *
   * @param column
   * @param direction
   * @returns
   */
  setSortOrder(
    column: StaticColumnDetails & MutableColumnDetails,
    direction: TableSortOrder,
  ) {
    if (!column.field) {
      return
    }

    this.searcher.setSortField(column.field)
    this.searcher.setSortDirection(direction)
  }

  /**
   * Gets the latest open ticket for the specified job.
   */
  getLatestOpenTicketForJob(job: SearchableJob) {
    return this.jobTicketStore.getLatestTicketForJob(job.id)
  }

  /**
   * Sets the table to only show jobs that are active (not archived).
   */
  @action.bound
  viewOnlyActiveJobs() {
    const filter = this.searcher.filters.getFilter(
      'archived',
    ) as FlagFilter<'archived'>
    filter.setValue(false)
  }

  /**
   * Sets the table to only show jobs that are archived.
   */
  @action.bound
  viewOnlyArchivedJobs() {
    const filter = this.searcher.filters.getFilter(
      'archived',
    ) as FlagFilter<'archived'>
    filter.setValue(true)
  }

  @action.bound
  private pushJob(job: SearchableJob) {
    if (!this.searcher.scroller.items.includes(job)) {
      this.searcher.scroller.items.unshift(job)
      this.searcher.totalJobs += 1
    }
  }

  /**
   * Activates the view model.
   */
  activate = task.resolved(async () => {
    await this.fetchUserTeamMembers()
    this.activateSearcher()
    this.columnManager.activate()
    this.selectionVM.bulkCopyVM.activate()

    if (!this.searcher.isUserTypePro) {
      const userId = this.getCurrentMember().user.publicId
      const savedWorkspaces = userId
        ? this.lastWorkspaceStorage.load(userId)
        : null

      // Always activate filter to enable persistence
      await this.searcher.workspacesFilter.activate()

      // Determine initial filter state
      // If global "default to all workspaces" is enabled, always use "All workspaces"
      // This takes priority over localStorage to ensure consistent behavior on page load
      const currentGlobalId = this.workspaceSelectorState.workspace?.id
      const savedGlobalSnapshot = appStorage.getItem(this.globalSnapshotKey)
      const globalChanged =
        currentGlobalId && savedGlobalSnapshot !== currentGlobalId

      if (this.workspaceSelectorState.isDefaultToAllWorkspaces) {
        this.searcher.workspacesFilter.change([])
      } else if (globalChanged) {
        // Global workspace changed since last visit — sync to it
        this.workspaceSelectorUpdates$.subscribe()
      } else if (
        savedWorkspaces &&
        savedWorkspaces !== ALL_WORKSPACES_SELECTION
      ) {
        // Global hasn't changed — restore per-page override from localStorage
        try {
          const workspaceIds = JSON.parse(savedWorkspaces) as string[]
          const validIds = workspaceIds.filter((id) =>
            this.searcher.workspacesFilter.options.some((o) => o.value === id),
          )
          if (validIds.length > 0) {
            this.searcher.workspacesFilter.change(validIds as Id[])
          } else {
            this.workspaceSelectorUpdates$.subscribe()
          }
        } catch {
          this.workspaceSelectorUpdates$.subscribe()
        }
      } else if (savedWorkspaces === ALL_WORKSPACES_SELECTION) {
        // "All workspaces" was explicitly saved previously
        this.searcher.workspacesFilter.change([])
      } else {
        // No saved selection, use global workspace
        this.workspaceSelectorUpdates$.subscribe()
      }

      // Save workspace changes (only when isDefaultToAllWorkspaces is disabled)
      if (userId) {
        this.workspaceReactionDisposer = reaction(
          () => this.searcher.workspacesFilter.selected.slice(),
          (selected) => {
            // Don't save if isDefaultToAllWorkspaces is enabled
            // This ensures the page always resets to "All workspaces" on next load
            if (this.workspaceSelectorState.isDefaultToAllWorkspaces) {
              return
            }
            if (selected.length === 0) {
              this.lastWorkspaceStorage.save(ALL_WORKSPACES_SELECTION, userId)
            } else {
              this.lastWorkspaceStorage.save(JSON.stringify(selected), userId)
            }
            // Snapshot the current global workspace so we can detect changes on next visit
            const globalId = this.workspaceSelectorState.workspace?.id
            if (globalId) {
              appStorage.setItem(this.globalSnapshotKey, globalId)
            }
          },
          { fireImmediately: false },
        )

        // React to global workspace selector changes
        this.globalWorkspaceReactionDisposer = reaction(
          () => this.workspaceSelectorState.workspace?.id,
          (workspaceId) => {
            if (
              workspaceId &&
              this.searcher.workspacesFilter.options.some(
                (o) => o.value === workspaceId,
              )
            ) {
              this.searcher.workspacesFilter.change([workspaceId as Id])
            }
          },
          { fireImmediately: false },
        )
      }
    }
  })

  /**
   * Deactivates the view model.
   */
  deactivate() {
    // We don't deactivate the searcher because we want
    // to keep the state for the searcher around.
    this.selectionVM.bulkCopyVM.deactivate()
    this.workspaceReactionDisposer?.()
    this.workspaceReactionDisposer = null
    this.globalWorkspaceReactionDisposer?.()
    this.globalWorkspaceReactionDisposer = null
  }
}

/**
 * Handles the selection logic and the available actions
 */
export class JobManagementSelectionViewModel {
  bulkActionErrorVM = new BulkActionJobErrorViewModel()

  bulkCopyJobsSuccessVM = new BulkCopyJobsSuccessViewModel(
    this.jobCreationDialogViewModel,
  )

  bulkCopyVM = new BulkCopyJobsViewModel(
    this.jobFetcher,
    this.newAlert,
    async (jobIds) => await this.jobStore.bulkCopyJobs(jobIds),
    this.pushJob,
    this.bulkActionErrorVM,
    this.bulkCopyJobsSuccessVM,
  )

  transferJobVM = new TransferJobViewModel(
    this.findMembers,
    this.findTeamMembersByUserPublicId,
    this.jobStore,
    this.providerStore,
    this.newAlert,
    undefined,
    this.bulkActionErrorVM,
  )

  /**
   * Confirmation shown before advancing the selected jobs.
   */
  confirmAdvanceJobs = new ConfirmViewModel()

  /**
   * Number of selected jobs that had not reached their advance date when the confirmation opened.
   */
  @observable
  earlyAdvanceJobsCount = 0

  @observable
  selectedJobs = new Set<SearchableJob>()

  constructor(
    private _getFilteredJobs: () => readonly SearchableJob[],
    private newAlert: AlertFactory,
    private jobStore: JobStore,
    private getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMemberships,
    private _getSelectedWorkspaces: () => string[],
    private _getArchivedWorkspaces: () => boolean,
    private removeJobs: (ids: string[]) => void,
    private jobFetcher: JobSearchAdapter,
    private pushJob: (job: SearchableJob) => void,
    public fetchAllJobs: Task<[], Promise<void>>,
    private _getWorkspacesFilter: () => SelectFilter<string, Id>,
    private findMembers: FindMembers,
    private findTeamMembersByUserPublicId: FindUserTeamMembersByUserPublicId,
    private providerStore: ProviderStore,
    private jobCreationDialogViewModel: JobCreationDialogViewModel,
  ) {
    makeObservable(this)

    // Clear selected jobs when workspaces change so that we don't bring over old selections.
    reaction(
      () => this.selectedWorkspaces,
      () => this.clearSelectedJobsForActions(),
    )
    // Clear selected jobs when the active filter changes so that we don't bring over old selections.
    reaction(
      () => this.archivedJobs,
      () => this.clearSelectedJobsForActions(),
    )
  }

  @computed
  get filteredJobs() {
    return this._getFilteredJobs()
  }

  @computed
  get selectedWorkspaces() {
    return this._getSelectedWorkspaces()
  }

  @computed
  get archivedJobs() {
    return this._getArchivedWorkspaces()
  }

  /**
   * Whether the user is permitted to view archived jobs.
   */
  @computed
  get canViewArchivedJobs() {
    const currentMemberships = this.getCurrentUserWorkspaceMemberships()

    if (this.selectedWorkspaces.length === 0) {
      return this.getCurrentUserWorkspaceMemberships().every((m) =>
        m.member.hasPermission('ADMIN_JOB_LIST'),
      )
    } else {
      return this.selectedWorkspaces.every((id) =>
        currentMemberships
          .find((m) => m.workspace.id === id)
          ?.member.hasPermission('ADMIN_JOB_LIST'),
      )
    }
  }

  @computed
  get canArchiveRestoreJobs() {
    const currentMemberships = this.getCurrentUserWorkspaceMemberships()

    if (this.selectedWorkspaces.length === 0) {
      return this.getCurrentUserWorkspaceMemberships().every((m) =>
        m.member.hasPermission('ADMIN_JOB_UPDATE'),
      )
    } else {
      return this.selectedWorkspaces.every((id) =>
        currentMemberships
          .find((m) => m.workspace.id === id)
          ?.member.hasPermission('ADMIN_JOB_UPDATE'),
      )
    }
  }

  /**
   * Returns whether scroller items are all selected, none selected, or in between.
   */
  @computed
  get jobActionSelectionState(): SelectionState {
    if (this.selectedJobs.size === 0) {
      return SelectionState.NoneSelected
    }

    if (this.filteredJobs.length === this.selectedJobs.size) {
      return SelectionState.AllSelected
    }

    if (this.selectedJobs.size > 0) {
      return SelectionState.SomeSelected
    }

    return SelectionState.NoneSelected
  }

  /**
   * Indicates the action to take for arching/restoring.
   */
  @computed
  get archiveRestoreAction(): ArchiveRestoreAction {
    if (this.selectedJobs.size === 0) {
      return { type: 'NoJobsSelected' }
    }

    return Array.from(this.selectedJobs.values()).some((job) => job.archived)
      ? { type: 'Restore' }
      : { type: 'Archive' }
  }

  @computed
  get canCloneJobs(): boolean {
    return Array.from(this.selectedJobs.values()).every((job) => job.canCopy)
  }

  @computed
  get canTransferJobs(): boolean {
    return Array.from(this.selectedJobs.values()).every(
      (job) => job.canTransferJobAsAdmin,
    )
  }

  /**
   * Whether every selected job is pending and can be advanced by the current member.
   */
  @computed
  get canAdvanceJobs(): boolean {
    return Array.from(this.selectedJobs.values()).every(
      (job) => job.canAdvanceJobAsAdmin,
    )
  }

  @action.bound
  async cloneJobs() {
    const ids = Array.from(this.selectedJobs.values()).map((x) => ({
      id: Id(x.id),
      shortId: x.shortId,
      displayName: x.displayName,
    }))
    this.bulkCopyVM.showConfirmation(ids)
  }

  @action.bound
  async archiveRestoreJobs() {
    const ids = Array.from(this.selectedJobs.values()).map((x) => x.id)

    switch (this.archiveRestoreAction.type) {
      case 'Archive':
        await this.jobStore
          .archiveJobs(ids)
          .then((res) => {
            this.removeJobs(res.archived)
            makeErrorAlert(res.archived, this.newAlert, 'archive')
          })
          .catch(() => makeErrorAlert([], this.newAlert, 'archive', true))
        break
      case 'Restore':
        await this.jobStore
          .restoreJobs(ids)
          .then((res) => {
            this.removeJobs(res.restored)
            makeErrorAlert(res.restored, this.newAlert, 'restore')
          })
          .catch(() => makeErrorAlert([], this.newAlert, 'archive', true))
        break
      case 'NoJobsSelected':
        return
    }

    this.clearSelectedJobsForActions()

    function makeErrorAlert(
      successfulIds: string[],
      newAlert: AlertFactory,
      action: 'archive' | 'restore',
      unknown: boolean = false,
    ) {
      if (unknown) {
        newAlert()
          .fromError(
            new Error(
              `An unknown failure occurred while attempting to ${action} jobs.`,
            ),
          )
          .show()
      } else if (ids.length !== successfulIds.length) {
        newAlert()
          .fromError(
            new Error(
              `Some jobs could not be ${action}ed. Try again and check permissions.`,
            ),
          )
          .show()
      }
    }
  }

  transferJobs = async () => {
    const jobs = Array.from(this.selectedJobs.values()).map((x) => x.id)
    this.transferJobVM.open(await this.jobStore.fetchJobs(jobs))
  }

  /**
   * Asks for confirmation, then advances the selected jobs.
   */
  @action.bound
  advanceJobs() {
    this.earlyAdvanceJobsCount = Array.from(this.selectedJobs.values()).filter(
      (job) =>
        isNotNil(job.dateAdvanceAllowed) && isFuture(job.dateAdvanceAllowed),
    ).length
    this.confirmAdvanceJobs.show(() => this.advanceSelectedJobs())
  }

  private async advanceSelectedJobs() {
    const jobs = Array.from(this.selectedJobs.values())

    const alert = this.newAlert()
      .withState(AlertState.Pending)
      .saying(
        'Advancing jobs',
        `Advancing ${jobs.length} ${pluralize('job', jobs.length)}...`,
      )
      .show()

    try {
      const response = await this.jobStore.bulkAdvanceJobs(
        jobs.map((j) => j.id),
      )

      const errorList = [
        ...response.skippedIds.map((jobId) => ({
          jobId,
          error: 'Job is not pending',
        })),
        ...response.errors,
      ]
      const advancedCount = response.advancedIds.length

      if (errorList.length > 0) {
        this.bulkActionErrorVM.showErrors(errorList, jobs)
      }

      this.clearSelectedJobsForActions()

      if (advancedCount === 0) {
        alert.dismiss()
        return
      }

      alert
        .withState(AlertState.Success)
        .saying(
          'Success!',
          `${advancedCount} ${pluralize('job', advancedCount)} ${pluralize('was', advancedCount, 'were')} advanced`,
        )
    } catch (e) {
      alert.fromError(e)
    }
  }

  /**
   * Add job to selected jobs.
   */
  @action.bound
  addJobToSelectedJobs(job: SearchableJob) {
    this.selectedJobs.add(job)
  }

  /**
   * Toggles whether all jobs are selected.
   */
  @action.bound
  toggleAllSelectedJobsForActions() {
    switch (this.jobActionSelectionState) {
      case SelectionState.NoneSelected:
      case SelectionState.SomeSelected:
        if (this._getWorkspacesFilter().selected.length !== 1) {
          this.filteredJobs.forEach((job) => this.addJobToSelectedJobs(job))
          break
        }

        this.fetchAllJobs().then(() =>
          this.filteredJobs.forEach((job) => this.addJobToSelectedJobs(job)),
        )
        break
      case SelectionState.AllSelected:
        this.clearSelectedJobsForActions()
        break
    }
  }

  /**
   * Clears the actions selection.
   */
  @action.bound
  clearSelectedJobsForActions() {
    this.selectedJobs.clear()
  }

  /**
   * Indicates whether a job is selected for actions.
   */
  isSelectedForActions(job: SearchableJob) {
    return this.selectedJobs.has(job)
  }

  /**
   * Toggles whether the job is selected for actions.
   */
  @action.bound
  toggleJobSelectionForActions(job: SearchableJob) {
    if (this.isSelectedForActions(job)) {
      this.selectedJobs.delete(job)
    } else {
      this.selectedJobs.add(job)
    }
  }
}
